import { FromMachineActionBase } from '../from-machine-action.base';
import {
  BookingParams,
  BookingPosition,
  BookingResult,
  StockTargetType,
} from '../../types';

/**
 * Maschinen-Quelle in der Unified-Welt. Zusätzlich zur geteilten Verschrottung
 * (M→BufferstockDisposal) bietet sie die Rücknahme aufs KFZ (M→KFZ als
 * einphasiges CarTransferMovement). Maschine → Bufferstock existiert bewusst
 * nicht: die Rücknahme wird beim Befüllen in die OrderMovement verrechnet (siehe
 * MachineFillBookingService).
 */
export class UnifiedFromMachineAction extends FromMachineActionBase {
  /**
   * Routet die Buchung nach `targetType` auf die Rücknahme aufs KFZ (M→KFZ)
   * und die geteilte Verschrottung (M→BufferstockDisposal); alle anderen Ziele
   * werden abgelehnt.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult der gewählten Richtung bzw. Fehler bei unzulässigem Ziel
   */
  public async execute(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    this.debugInfo('[from-machine:unified]', position, params);
    switch (params.context.booking.targetType) {
      case StockTargetType.CAR:
        return this.toCar(position, params);
      case StockTargetType.BUFFERSTOCK_DISPOSAL:
        return this.toBufferstockDisposal(position, params);
      case StockTargetType.BUFFERSTOCK:
        return this.failure(
          'Maschine → Bufferstock ist keine valide Direction (Rücknahme wird beim Befüllen verrechnet)',
        );
      case StockTargetType.MACHINE:
        return this.failure('Maschine → Maschine ist keine valide Direction');
      case StockTargetType.TRASH_BIN:
        return this.failure('Maschine → Müll ist keine valide Direction');
      case StockTargetType.CONSUMPTION_BIN:
        return this.failure('Maschine → Verbrauch ist keine valide Direction');
      default:
        return this.exhaustiveFailure(params.context.booking.targetType);
    }
  }

  /**
   * Maschine → KFZ: Rücknahme von Füllware aufs Fahrzeug (Maschinenlager gibt
   * ab, Fahrzeuglager bekommt). Erzeugt ein einphasiges CarTransferMovement.
   *
   * @param position Zu buchende Position (article_id und amount)
   * @param params Buchungskontext, Adapter und stabile client_ids
   * @returns BookingResult; Fehler bei fehlendem Fahrzeuglager, amount <= 0, fehlendem Maschinenlager oder Speicher-/Update-Fehlschlag
   */
  protected async toCar(
    position: BookingPosition,
    params: BookingParams,
  ): Promise<BookingResult> {
    const { context, adapters } = params;
    const { booking } = context;

    // Ohne lokales Fahrzeuglager keine Basis → früh ablehnen
    if (!adapters.car.hasLocalInventory()) {
      return this.failure('Maschine->KFZ: kein lokales Fahrzeuglager vorhanden');
    }
    if (position.amount <= 0) {
      return this.failure('Maschine->KFZ: amount muss > 0 sein');
    }

    // Die Movement-Source braucht die StockObject-ID des Maschinenlagers, nicht
    // die Maschinen-ID (booking.sourceId). Das lokale Maschinen-Update unten
    // läuft weiter über die Maschinen-ID (der MachineAdapter löst selbst auf).
    const machineId = booking.sourceId;
    const stockObjectId =
      await adapters.machine.findStockObjectIdLocal(machineId);
    if (stockObjectId === null) {
      return this.failure(
        `Maschine->KFZ: Maschine ${machineId} hat kein Lager (StockObject)`,
      );
    }

    // 1. Warehouse: CarTransferMovement in die Offline-Queue (einphasig).
    const movement = adapters.movement.build(
      position,
      { ...booking, sourceId: stockObjectId },
      params.clientIds.idFor(position),
    );
    const queued = await adapters.movement.enqueue(movement);
    if (!queued.value) {
      return this.failure(
        'CarTransferMovement konnte nicht in der Queue gespeichert werden',
        queued.retryable,
      );
    }
    // 2. Maschinenlager lokal verringern (Quelle gibt amount ab).
    const machineUpdated = await adapters.machine.updatePositionAmountLocal(
      position,
      machineId,
      -position.amount,
    );
    if (!machineUpdated) {
      return this.failure(
        `Maschine ${machineId} konnte nicht aktualisiert werden`,
      );
    }
    // 3. Car: lokales Fahrzeuglager erhöhen (Fahrzeug bekommt amount).
    const carUpdated = await adapters.car.updatePositionAmountLocal(
      position,
      +position.amount,
    );
    if (!carUpdated) {
      return this.failure('Car-Bestand konnte nicht aktualisiert werden');
    }
    return this.success();
  }
}
